// SolyticsList.jsx
// @ts-nocheck
/* eslint-disable react/prop-types */
/**
 * SolyticsList.jsx
 * ASSEMBLY COMPONENT
 * A fully data-driven list component that intelligently routes to either a standard
 * or virtualized rendering engine based on dataset size.
 *
 * A fully data-driven list component. Pass an array of item objects and
 * everything else — rendering, interaction, accessibility — is handled internally.
 * SolyticsListItem is an implementation detail; developers never touch it directly.
 *
 * ─── ITEM OBJECT SCHEMA ────────────────────────────────────────────────────────
 *
 * {
 *   // Identity (required)
 *   id:              'unique-id',
 *
 *   // Content
 *   label:           'Display Name',      ← primary text
 *   secondaryLabel:  'Subtitle text',     ← secondary/helper text (optional)
 *   icon:            <SomeIcon />,        ← leading icon (optional)
 *   endIcon:         <ChevronRight />,    ← trailing icon (optional)
 *   secondaryAction: <IconButton />,      ← far-right action slot (optional)
 *
 *   // Behaviour
 *   // NOTE: no `button` prop — interactivity is inferred from onClick / href.
 *   //       If either is present, the item renders as a button automatically.
 *   onClick:         () => {},            ← makes item interactive
 *   href:            '/some-path',        ← renders as <a>, also makes interactive
 *   selected:        false,
 *   disabled:        false,
 *   autoFocus:       false,
 *
 *   // Appearance
 *   divider:         false,
 *   dense:           false,              ← overrides list-level dense per item
 *
 *   // Accessibility
 *   ariaLabel:       'descriptive label',
 *   ariaDescribedBy: 'element-id',
 *   role:            'menuitem',         ← override default role
 *   tabIndex:        0,
 *
 *   // Escape hatch — bypasses all of the above, renders raw JSX inside <ListItem>
 *   custom:          <MyLayout />,
 * }
 *
 * ─── EXAMPLES ──────────────────────────────────────────────────────────────────
 *
 * // Basic static list
 * <SolyticsList items={[
 *   { id: '1', label: 'Apples' },
 *   { id: '2', label: 'Oranges' },
 * ]} />
 *
 * // Interactive list with icons
 * <SolyticsList items={[
 *   { id: '1', label: 'Dashboard', icon: <DashboardIcon />, onClick: () => navigate('/') },
 *   { id: '2', label: 'Settings',  icon: <SettingsIcon />,  onClick: () => navigate('/settings'), selected: true },
 *   { id: '3', label: 'Logout',    icon: <LogoutIcon />,    onClick: handleLogout, divider: true },
 * ]} />
 *
 * // Mixed list — some items clickable, some static
 * <SolyticsList items={[
 *   { id: '1', label: 'Section Header' },
 *   { id: '2', label: 'Clickable Item', onClick: handleClick },
 *   { id: '3', label: 'Disabled Item',  onClick: handleClick, disabled: true },
 * ]} />
 *
 * // Link-based navigation list
 * <SolyticsList items={[
 *   { id: '1', label: 'Home',     href: '/' },
 *   { id: '2', label: 'About',    href: '/about' },
 *   { id: '3', label: 'Contact',  href: '/contact' },
 * ]} />
 *
 * // Scrollable list (scrolls automatically when parent has constrained height)
 * <SolyticsList items={longItemsArray} />
 *
 * // Horizontal list
 * <SolyticsList items={tabItems} orientation="horizontal" disablePadding />
 *
 * // Custom escape hatch for unusual layouts
 * <SolyticsList items={[
 *   { id: '1', label: 'Normal item' },
 *   { id: '2', custom: <MyComplexLayout /> },
 * ]} />
 */

import React, { useCallback, useRef, useState, useMemo } from 'react';
import { List, ListSubheader } from '@mui/material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import SolyticsListStandard from './Internals/SolyticsList/SolyticsListStandard';
import SolyticsListVirtualized from './Internals/SolyticsList/SolyticsListVirtualized';

const DEFAULT_DISABLE_PADDING = false;
const DEFAULT_DENSE = false;
const DEFAULT_SUBHEADER = null;
const DEFAULT_ORIENTATION = 'vertical';
const DEFAULT_COMPONENT = 'ul';
const DEFAULT_ROLE = 'list';

const AUTO_VIRTUALIZE_THRESHOLD = 100;

/**
 * Get list container styles
 * @param {boolean} dense - Dense state
 * @param {boolean} disablePadding - Disable padding state
 * @param {string} orientation - Orientation (vertical or horizontal)
 * @returns {function} Style function for theme
 */
const getListStyles = (dense, disablePadding, orientation) => (theme) => {
  const isHorizontal = orientation === 'horizontal';

  return {
    width: '100%',
    position: 'relative',
    padding: disablePadding ? 0 : dense ? '4px 0' : '8px 0',
    listStyle: 'none',
    backgroundColor: theme.palette.background.paper,

    // Horizontal orientation styles
    ...(isHorizontal && {
      display: 'flex',
      flexDirection: 'row',
      padding: disablePadding ? 0 : dense ? '0 4px' : '0 8px',
    }),

    // Subheader spacing
    '& .MuiListSubheader-root': {
      backgroundColor: theme.palette.background.paper,
      lineHeight: '48px',
      fontSize: '12px',
      fontWeight: 600,
      letterSpacing: '0.05em',
      color: theme.palette.text.secondary,
      padding: '0 16px',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },

    // Accessibility focus styles
    '&:focus': { outline: 'none' },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: '2px',
    },
  };
};

/**
 * SolyticsList Component
 *
 * A fully data-driven list component that handles rendering, interaction, and accessibility internally.
 *
 * @param {Object} props - The component props.
 * @param {Array<Object>} [props.items=[]] - Array of item objects to render in the list.
 * @param {boolean} [props.disablePadding=false] - If true, vertical padding is removed from the list.
 * @param {boolean} [props.dense=false] - If true, compact vertical padding designed for keyboard and mouse input is used.
 * @param {'rounded'|'flush'} [props.variant='rounded'] - Visual style of the list items. 'rounded' applies a border radius, 'flush' removes it.
 * @param {'vertical'|'horizontal'} [props.orientation='vertical'] - The orientation of the list.
 * @param {React.ReactNode|string} [props.subheader=null] - The content of the subheader, normally `ListSubheader`.
 * @param {React.ElementType} [props.component='ul'] - The component used for the root node. Either a string to use a HTML element or a component.
 * @param {string} [props.id] - The id of the list component.
 * @param {string} [props.role='list'] - ARIA role attribute.
 * @param {string} [props.ariaLabel] - ARIA label attribute.
 * @param {string} [props.ariaLabelledBy] - ARIA labelledby attribute.
 * @param {string} [props.ariaDescribedBy] - ARIA describedby attribute.
 * @param {number} [props.tabIndex] - Tab index attribute.
 * @param {boolean} [props.enableDragAndDrop=false] - If true, enables dragging items to reorder them via HTML5 DND API.
 * @param {Function} [props.onReorder] - Callback fired when an item is dropped. Signature: `(sourceIndex, destinationIndex)`.
 * @param {number} [props.listVirtualizationThreshold=AUTO_VIRTUALIZE_THRESHOLD] - If provided, large lists will be virtualized correctly.
 * @param {number|string} [props.gap] - Gap between list items.
 * @returns {React.ReactElement} The rendered list component.
 */
const SolyticsList = ({
  items = [],
  disablePadding = DEFAULT_DISABLE_PADDING,
  variant = 'rounded',
  dense = DEFAULT_DENSE,
  orientation = DEFAULT_ORIENTATION,
  subheader = DEFAULT_SUBHEADER,
  component = DEFAULT_COMPONENT,
  id,
  role = DEFAULT_ROLE,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  tabIndex,
  enableDragAndDrop = false,
  onReorder,
  listVirtualizationThreshold = AUTO_VIRTUALIZE_THRESHOLD,
  gap,
}) => {
  const listRef = useRef(null);

  const [listElement, setListElement] = useState(null);

  const handleListRef = useCallback((node) => {
    listRef.current = node; // Keep ref for internal keyboard nav
    setListElement(node); // Trigger state update for the child engine
  }, []);

  const isVirtualized = useMemo(() => {
    if (enableDragAndDrop || orientation === 'horizontal') return false;
    if (!items || items.length === 0) return false;
    return items.length >= listVirtualizationThreshold;
  }, [listVirtualizationThreshold, enableDragAndDrop, orientation, items]);

  // ── Keyboard Navigation ──────────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (event) => {
      if (!listRef.current) return;
      const isVertical = orientation === 'vertical';

      // Find all focusable items within the list
      const focusableElements = Array.from(
        listRef.current.querySelectorAll(
          '[role="button"], [role="menuitem"], [role="option"], a[href], button, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(
        (el) => !el.disabled && el.closest('li') !== null && el.tabIndex >= 0
      );

      if (focusableElements.length === 0) return;

      const currentIndex = focusableElements.indexOf(document.activeElement);
      let nextIndex = currentIndex;

      switch (event.key) {
        case isVertical ? 'ArrowDown' : 'ArrowRight':
          event.preventDefault();
          nextIndex = (currentIndex + 1) % focusableElements.length;
          break;
        case isVertical ? 'ArrowUp' : 'ArrowLeft':
          event.preventDefault();
          // If no element is focused yet, start at the first
          nextIndex =
            currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
          break;
        case 'Home':
          event.preventDefault();
          nextIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          nextIndex = focusableElements.length - 1;
          break;
        case 'Escape':
          event.preventDefault();
          document.activeElement?.blur?.();
          return;
        case 'Enter':
        case ' ':
          // Default browser behavior clicks the focused interactive element
          return;
        default:
          return;
      }

      if (nextIndex !== currentIndex && focusableElements[nextIndex]) {
        focusableElements[nextIndex].focus();

        // Scroll element into view if virtualized or overflow is enabled
        focusableElements[nextIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    },
    [orientation]
  );

  const handleDrop = useCallback(
    (dragIndex, dropIndex) => {
      if (!enableDragAndDrop) return;
      if (dragIndex !== null && dragIndex !== dropIndex) {
        onReorder?.(dragIndex, dropIndex);
      }
    },
    [enableDragAndDrop, onReorder]
  );

  // ── Subheader normalisation ────────────────────────────────────────────────
  // Developers can pass a plain string for convenience; we wrap it automatically.
  const resolvedSubheader =
    subheader && typeof subheader === 'string' ? (
      <ListSubheader disableSticky>{subheader}</ListSubheader>
    ) : (
      subheader
    );

  const listProps = {
    id,
    ref: handleListRef, // Pass our dual-handler callback
    role,
    component: isVirtualized ? 'div' : component,
    disablePadding,
    dense,
    subheader: resolvedSubheader,
    tabIndex,
    onKeyDown: handleKeyDown,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-orientation': orientation,
    sx: (theme) => getListStyles(dense, disablePadding, orientation)(theme),
  };

  const renderedContent = isVirtualized ? (
    <SolyticsListVirtualized
      items={items}
      variant={variant}
      dense={dense}
      gap={gap}
      enableDragAndDrop={enableDragAndDrop}
      handleDrop={handleDrop}
      listElement={listElement} // Pass the state-driven element
    />
  ) : (
    <SolyticsListStandard
      items={items}
      variant={variant}
      dense={dense}
      gap={gap}
      orientation={orientation}
      enableDragAndDrop={enableDragAndDrop}
      handleDrop={handleDrop}
    />
  );

  if (enableDragAndDrop && !isVirtualized) {
    return (
      <DndProvider backend={HTML5Backend}>
        <List {...listProps}>{renderedContent}</List>
      </DndProvider>
    );
  }

  return <List {...listProps}>{renderedContent}</List>;
};

export default SolyticsList;
